import { FileOutlined, InboxOutlined } from "@ant-design/icons"
import { App, Checkbox, Flex, Form, Typography, Upload } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../Components/Button"
import CountrySelect from "../../../../../Components/CountrySelect"
import InfoTooltip from "../../../../../Components/InfoTooltip/InfoTooltip"
import Input from "../../../../../Components/Input"
import TextArea from "../../../../../Components/Input/TextArea"
import PhoneInput from "../../../../../Components/PhoneInput"
// DeflectPage/services.js and moved into Configure during the CHA1-1769 merge).
import { uploadLogo } from "../../constants/externalOps"
import { getValueAt, setValueAt } from "../../utils/path"
import { validateQuestion } from "../../utils/sectionValidation"
import { LOGO_DIMENSION_PX, LOGO_MIME, MAX_LOGO_BYTES, readImageDimensions } from "./helpers"
import TextMultiControl from "./TextMultiControl"

import styles from "./questionField.module.less"

const { Text } = Typography

const QuestionField = ({ question, sectionState, onChange, showErrors, readOnly }) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const value = getValueAt(sectionState, question.key)

  // Inputs are fully controlled off `value` (Redux) so async profile hydration
  // wins over a local initializer. No-op while read-only (view-only role,
  // under-review lock, or Step 3 after the first submission -
  // §11.9/11.14/11.15).
  const flush = (next) => {
    if (readOnly) return
    onChange(setValueAt(sectionState, question.key, next))
  }

  // Logo upload (§11.8): validate PNG / exactly 320x320 / <=10 MB before ever
  // calling the upload stub, then write its result straight into this field's
  // value — no Redux round-trip needed.
  const handleLogoUpload = async (file) => {
    if (file.type !== LOGO_MIME) {
      notification.error({ message: t("Please upload a PNG image"), placement: "bottomRight" })
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      notification.error({ message: t("File must be under 10 MB"), placement: "bottomRight" })
      return
    }
    const dims = await readImageDimensions(file)
    if (!dims || dims.width !== LOGO_DIMENSION_PX || dims.height !== LOGO_DIMENSION_PX) {
      notification.error({
        message: t("Image must be exactly 320×320 px"),
        placement: "bottomRight"
      })
      return
    }
    const uploaded = await uploadLogo(file)
    flush(uploaded)
  }

  const errorKey = validateQuestion(question, value)
  const validateStatus = showErrors && errorKey ? "error" : ""
  // Params cover every interpolated error key in use (max-limit, set-size); an
  // unused key is simply ignored by i18next.
  const help =
    showErrors && errorKey ? t(errorKey, { max: question?.max, count: question?.minItems }) : null

  const ph = question.placeholder
    ? t(question.placeholder, {
        nsSeparator: false,
        keySeparator: false,
        defaultValue: question.placeholder
      })
    : undefined

  let control = null
  switch (question.type) {
    case "text":
    case "url": {
      // §11.4: URL fields are not auto-normalized — no https:// auto-prepend.
      //
      // `softMax` fields carry a limit but no input-level `maxLength`: the
      // browser would otherwise stop the keystroke and silently drop the rest
      // of what the merchant pasted. They get the "Maximum N characters"
      // error on Next instead, so the value stays whole and the merchant is
      // told why it is rejected.
      control = (
        <Input
          value={value ?? ""}
          placeholder={ph}
          maxLength={question.softMax ? undefined : question.max}
          disabled={readOnly}
          onChange={(e) =>
            flush(question.uppercase ? e.target.value.toUpperCase() : e.target.value)
          }
          status={validateStatus}
        />
      )
      break
    }
    case "textarea": {
      control = (
        <div className={styles.textareaWrap}>
          <TextArea
            value={value ?? ""}
            placeholder={ph}
            maxLength={question.max}
            showCount={!!question.max}
            rows={4}
            disabled={readOnly}
            onChange={(e) => flush(e.target.value)}
            status={validateStatus}
          />
        </div>
      )
      break
    }
    case "country": {
      // Stores ISO2 only. The backend's paired `country` (display name) column
      // is derived from the countries list at save time — see
      // externalOps.flattenProfile — so it doesn't need to live in wizard
      // state.
      control = (
        <CountrySelect
          value={value || ""}
          disabled={readOnly}
          status={validateStatus}
          placeholder={ph || t("Select country")}
          onChange={(country) => flush(country?.country_code || "")}
        />
      )
      break
    }
    case "phone": {
      // PhoneInput reads the country from `countryCode` and falls back to the
      // browser's country when the value names none.
      control = <PhoneInput value={value} disabled={readOnly} onChange={(v) => flush(v)} />
      break
    }
    case "text-multi": {
      const arr = Array.isArray(value) ? value : value ? [String(value)] : []

      control = (
        <TextMultiControl
          t={t}
          values={arr}
          addLabel={question.addLabel}
          placeholder={ph}
          validateValue={question.validateValue}
          uppercase={question.uppercase}
          readOnly={readOnly}
          onChange={(next) => flush(next)}
        />
      )
      break
    }
    case "checkbox": {
      control = (
        <Checkbox checked={!!value} disabled={readOnly} onChange={(e) => flush(e.target.checked)}>
          {t(question.label)}
        </Checkbox>
      )
      break
    }
    case "upload": {
      const preview = value?.url
      const fileName = value?.file_name
      const accept = question.accept || "image/png,image/jpeg,application/pdf"
      const hint = question.acceptHint || "PNG, JPG or PDF. Max 5 MB."
      // blob: covers the offline upload stub's URL.createObjectURL preview
      // (externalOps.uploadLogo, §11.8); data:/file-extension cover a real
      // upload response.
      const isImagePreview =
        preview &&
        (/^data:image/i.test(preview) ||
          /^blob:/i.test(preview) ||
          /\.(png|jpe?g|svg|gif|webp)(\?|$)/i.test(preview))

      control = (
        <div className={styles.upload}>
          <Upload.Dragger
            multiple={false}
            maxCount={1}
            showUploadList={false}
            accept={accept}
            disabled={readOnly}
            beforeUpload={(file) => {
              if (readOnly) return false
              handleLogoUpload(file)
              return false // we always handle the file ourselves, never antd's own uploader
            }}
          >
            {preview ? (
              <Flex align="center" gap={16} className={styles.uploadFilled}>
                {isImagePreview ? (
                  <img
                    src={preview}
                    alt={fileName || t(question.label)}
                    className={styles.uploadThumb}
                  />
                ) : (
                  <div className={styles.uploadFileIcon}>
                    <FileOutlined />
                  </div>
                )}
                <Flex vertical align="flex-start" gap={2} className={styles.uploadFilledMeta}>
                  {fileName ? <Text className={styles.uploadFileName}>{fileName}</Text> : null}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("Click to replace")}
                  </Text>
                </Flex>
                {readOnly ? null : (
                  <Button
                    type="text"
                    className="compact-btn"
                    text={t("Remove")}
                    onClick={(e) => {
                      e.stopPropagation()
                      flush(null)
                    }}
                  />
                )}
              </Flex>
            ) : (
              <>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t("Click or drag a file here to upload")}</p>
                <p className="ant-upload-hint">{t(hint)}</p>
              </>
            )}
          </Upload.Dragger>
        </div>
      )
      break
    }
    default:
      control = <Text type="secondary">{t("Unsupported question type")}</Text>
  }

  // A checkbox renders its own label as its children (antd convention), so the
  // generic top label is skipped for it — showing both would repeat the text.
  const isCheckbox = question.type === "checkbox"

  return (
    <Form.Item
      data-testid={`deflect-configure-field-${question.key}`}
      label={
        isCheckbox ? null : (
          <span className={styles.label}>
            <span className={styles.labelRow}>
              <span>{t(question.label)}</span>
              {question.required ? (
                <span
                  className={styles.requiredTag}
                  aria-label={t("Required")}
                  title={t("Required")}
                >
                  *
                </span>
              ) : null}
              {question.tooltip ? (
                <InfoTooltip className={styles.labelTip} title={t(question.tooltip)} />
              ) : null}
            </span>
          </span>
        )
      }
      validateStatus={validateStatus}
      className={styles.formItem}
      colon={false}
    >
      {control}
      {help ? (
        <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
          {help}
        </Text>
      ) : question.helpLinkText ? (
        <Text type="secondary" className={styles.help} style={{ marginTop: 4, display: "block" }}>
          {t(question.helpText)}{" "}
          {/* The spec names this link without giving a destination, so the
              schema supplies one only when the platform already publishes a
              confirmed URL for the same question. Without one the text still
              renders (as a non-navigating button) rather than blocking the
              field. */}
          {question.helpLinkHref ? (
            <a
              href={question.helpLinkHref}
              target="_blank"
              rel="noreferrer"
              className={styles.inlineLink}
            >
              {t(question.helpLinkText)}
            </a>
          ) : (
            <button type="button" className={styles.inlineLink}>
              {t(question.helpLinkText)}
            </button>
          )}
        </Text>
      ) : question.help ? (
        <Text type="secondary" className={styles.help} style={{ marginTop: 4, display: "block" }}>
          {t(question.help)}
        </Text>
      ) : null}
    </Form.Item>
  )
}

export default QuestionField
